import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Stack } from '@atlaskit/primitives';

import { ChoreographerAssetsContextProvider } from '@confluence/choreographer-services/entry-points/ChoreographerAssetsContext.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

export type RemoveAllSpecificAccessConfirmDialogProps = {
	isOpen: boolean;
	isConfirmDisabled: boolean;
	onCancel: () => void;
	onConfirm: () => void;
};

const i18n = defineMessages({
	heading: {
		id: 'share-and-restrict-dialog.specific-access-dialog.remove-all-confirm.heading',
		defaultMessage: 'Remove all users?',
		description:
			'Heading for the confirmation dialog shown before removing all principals from specific access',
	},
	body: {
		id: 'share-and-restrict-dialog.specific-access-dialog.remove-all-confirm.body',
		defaultMessage:
			'This action will revoke all specific access for all users currently assigned. If the general access is open they may be able to access it still.',
		description:
			'Body for the confirmation dialog shown before removing all principals from specific access',
	},
	boldBodyText: {
		id: 'share-and-restrict-dialog.specific-access-dialog.remove-all-confirm.bold-body-text',
		defaultMessage: 'This change cannot be undone.',
		description:
			'Body for the confirmation dialog shown before removing all principals from specific access',
	},
	cancelButton: {
		id: 'share-and-restrict-dialog.specific-access-dialog.remove-all-confirm.cancel',
		defaultMessage: 'Cancel',
		description: 'Cancel button label for remove-all confirmation dialog',
	},
	confirmButton: {
		id: 'share-and-restrict-dialog.specific-access-dialog.remove-all-confirm.confirm',
		defaultMessage: 'Confirm',
		description: 'Confirm button label for remove-all confirmation dialog',
	},
});

const RemoveAllSpecificAccessConfirmDialogContent: FC<
	RemoveAllSpecificAccessConfirmDialogProps
> = ({ isOpen, isConfirmDisabled, onCancel, onConfirm }) => {
	const { Modal } = useChoreographerAssets();

	if (!isOpen) {
		return null;
	}

	return (
		<Modal width="small" onClose={onCancel}>
			<ModalHeader>
				<ModalTitle>
					<FormattedMessage {...i18n.heading} />
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<FormattedMessage {...i18n.body} />
					<b>
						<FormattedMessage {...i18n.boldBodyText} />
					</b>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Button onClick={onCancel}>
					<FormattedMessage {...i18n.cancelButton} />
				</Button>
				<Button appearance="primary" isDisabled={isConfirmDisabled} onClick={onConfirm}>
					<FormattedMessage {...i18n.confirmButton} />
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export const RemoveAllSpecificAccessConfirmDialog: FC<RemoveAllSpecificAccessConfirmDialogProps> = (
	props,
) => {
	return (
		<ChoreographerAssetsContextProvider>
			<RemoveAllSpecificAccessConfirmDialogContent {...props} />
		</ChoreographerAssetsContextProvider>
	);
};
